import {
  LayoutDashboard,
  Inbox,
  Users,
  UserRound,
  Calendar,
  CreditCard,
  Globe,
  FileText,
  Settings,
} from "lucide-react";
import { useLocation } from "react-router";
import { useSidebar } from "./SidebarProvider";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { NavItem } from "./NavItem";
import { SearchStub } from "./SearchStub";
import { UserFooter } from "./UserFooter";
import { useActiveTenantOrNull } from "../../auth/AuthProvider";
import { useBranding } from "../../branding/BrandingProvider";

const NAV_PRIMARY = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/leads", label: "Leads", icon: Inbox },
  {
    to: "/clients",
    label: "Clients",
    icon: Users,
    // Clients and People are two directories of the same section, so the switch
    // between them is navigation and belongs here rather than on the page.
    children: [{ to: "/clients/people", label: "People", icon: UserRound }],
  },
  { to: "/sessions", label: "Sessions", icon: Calendar },
  { to: "/payments", label: "Payments", icon: CreditCard },
] as const;

const NAV_BUSINESS = [
  { to: "/website", label: "Website", icon: Globe },
  { to: "/billing", label: "Billing", icon: FileText },
  { to: "/settings", label: "Settings", icon: Settings },
] as const;

export function Sidebar() {
  const { expanded, mobileOpen } = useSidebar();
  const { pathname } = useLocation();
  const isLg = useMediaQuery("(min-width: 1024px)");
  const isMd = useMediaQuery("(min-width: 768px)");
  const { branding, loading } = useBranding();
  const tenantName = useActiveTenantOrNull()?.name ?? null;

  // Determine which logo to show
  // 1. If branding has a logoUrl, use it
  // 2. Otherwise fall back to tenant name text display for unbranded tenants
  const brandingLogoUrl = branding?.logoUrl;
  const lockupLabel = tenantName ?? "portal";

  // Determine visual state
  // lg+: always expanded (236px), always visible
  // md (768-1023): collapsed rail (64px) by default, expanded (236px) when expanded=true (overlay)
  // <md: hidden by default, expanded (236px) when mobileOpen=true (overlay drawer)
  const isExpanded = isLg || (isMd && expanded) || (!isMd && mobileOpen);
  const collapsed = !isExpanded;

  // Width: 236px expanded, 64px collapsed rail (md only), 236px for mobile drawer
  const widthClass = isExpanded ? "w-[236px]" : "w-16";

  // Positioning: at <md, translate off-screen when hidden
  const translateClass =
    !isMd && !mobileOpen ? "-translate-x-full" : "translate-x-0";

  return (
    <aside
      className={`fixed top-[var(--banner-h)] z-50 flex h-[calc(100vh-var(--banner-h))] flex-col bg-canvas dark:bg-canvas/60 dark:backdrop-blur-xl hair-r sidebar-transition ${widthClass} ${translateClass}`}
    >
      {/* Brand lockup */}
      <div className="flex items-center justify-center px-3 pb-4 pt-5">
        {loading ? (
          // Show placeholder while loading
          <div className={`animate-pulse rounded-md bg-ink-5 ${collapsed ? "h-8 w-8" : "h-10 w-32"}`} />
        ) : brandingLogoUrl ? (
          // Show tenant logo, centered and sized to fill the lockup area.
          // object-contain + max-w-full keeps the aspect ratio while capping
          // height (expanded) / footprint (collapsed rail).
          <img
            src={brandingLogoUrl}
            alt={tenantName ? `${tenantName} logo` : "Brand logo"}
            className={`w-auto object-contain ${collapsed ? "max-h-8 max-w-10" : "max-h-14 max-w-full"}`}
            loading="eager"
          />
        ) : (
          // Fall back to tenant name text for unbranded tenants
          <div className="font-display text-[18px] font-medium leading-none text-ink-4 text-center">
            {lockupLabel}
          </div>
        )}
      </div>

      {/* Search */}
      <SearchStub collapsed={collapsed} />

      {/* Navigation */}
      <nav aria-label="Main" className="flex-1 space-y-0.5 overflow-y-auto px-3">
        {NAV_PRIMARY.map((item) => {
          const children = "children" in item ? item.children : undefined;
          const inSection =
            pathname === item.to || pathname.startsWith(`${item.to}/`);
          return (
            <div key={item.to} className="space-y-0.5">
              <NavItem
                to={item.to}
                label={item.label}
                icon={item.icon}
                collapsed={collapsed}
                // A section with children keeps the parent highlighted on its own
                // detail routes, but hands the highlight to a child on theirs.
                activeWhen={
                  children
                    ? (p) =>
                        inSection &&
                        !children.some(
                          (c) => p === c.to || p.startsWith(`${c.to}/`),
                        )
                    : undefined
                }
              />
              {/* Children appear only inside their section — and stay rendered
                  when the rail is collapsed, or they'd be unreachable on tablet. */}
              {children &&
                inSection &&
                children.map((child) => (
                  <NavItem
                    key={child.to}
                    to={child.to}
                    label={child.label}
                    icon={child.icon}
                    collapsed={collapsed}
                    nested
                  />
                ))}
            </div>
          );
        })}
        {!collapsed && (
          <div className="px-3 pb-1 pt-4 text-[10.5px] font-medium uppercase tracking-wider text-ink-4">
            Business
          </div>
        )}
        {collapsed && <div className="my-2" />}
        {NAV_BUSINESS.map((item) => (
          <NavItem
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            collapsed={collapsed}
          />
        ))}
      </nav>

      {/* User footer */}
      <UserFooter collapsed={collapsed} />
    </aside>
  );
}
